import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Avatar } from "@mui/material";

const blobUrlCache = new Map();
const inflightRequests = new Map();

const PROFILE_PHOTO_DOCUMENT_TYPE = 10;

const loadBlobUrl = async (fileId) => {
  if (!fileId || fileId <= 0) {
    return null;
  }

  if (blobUrlCache.has(fileId)) {
    return blobUrlCache.get(fileId);
  }

  if (inflightRequests.has(fileId)) {
    return inflightRequests.get(fileId);
  }

  const request = axios
    .get(`/api/v1/azureblobstorage/${fileId}`, { responseType: "blob" })
    .then((response) => {
      const url = URL.createObjectURL(response.data);
      blobUrlCache.set(fileId, url);
      inflightRequests.delete(fileId);
      return url;
    })
    .catch(() => {
      inflightRequests.delete(fileId);
      return null;
    });

  inflightRequests.set(fileId, request);
  return request;
};

export const invalidateAvatarCache = (fileId) => {
  if (!fileId) {
    return;
  }

  const cachedUrl = blobUrlCache.get(fileId);
  if (cachedUrl) {
    URL.revokeObjectURL(cachedUrl);
    blobUrlCache.delete(fileId);
  }
};

const getInitials = (displayName) => {
  if (!displayName) {
    return "?";
  }

  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }

  if (parts.length === 1) {
    const word = parts[0];
    return word.length >= 2 ? word.substring(0, 2).toUpperCase() : word.charAt(0).toUpperCase();
  }

  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const UserAvatar = ({ profilePhotoFileId, displayName, size = 40, sx = {} }) => {
  const [imageUrl, setImageUrl] = useState(null);

  const initials = useMemo(() => getInitials(displayName), [displayName]);

  useEffect(() => {
    let isMounted = true;

    if (!profilePhotoFileId || profilePhotoFileId <= 0) {
      setImageUrl(null);
      return undefined;
    }

    loadBlobUrl(profilePhotoFileId).then((url) => {
      if (isMounted) {
        setImageUrl(url);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [profilePhotoFileId]);

  return (
    <Avatar
      src={imageUrl || undefined}
      alt={displayName || "User avatar"}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        fontWeight: 600,
        flexShrink: 0,
        ...sx,
      }}
    >
      {initials}
    </Avatar>
  );
};

export { PROFILE_PHOTO_DOCUMENT_TYPE };
export default UserAvatar;
